import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration, IBlackbaudFund } from '@feathr/blackbox';
import { Button, Modal, toast } from '@feathr/components';
import { useAccount } from '@feathr/extender/state';
import { logUserEvents } from '@feathr/hooks';

import BBFundSelect from './BBFundSelect';

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function EnableDonationSyncModal({
  integration,
  opened,
  onClose,
  onSuccess,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const [selectedFund, setSelectedFund] = useState<IBlackbaudFund | undefined>();

  useEffect(() => {
    if (!opened) {
      setSelectedFund(undefined);
    }
  }, [opened]);

  function handleFundChange(fund: IBlackbaudFund | undefined): void {
    setSelectedFund(fund);
  }

  async function handleConfirm(): Promise<void> {
    if (!selectedFund) {
      return;
    }

    try {
      integration.set({
        upsync_activities: {
          ...integration.get('upsync_activities'),
          donations: true,
        },
        fundraising: {
          fund_id: selectedFund.id,
        },
      });
      await integration.patchDirty();
      logUserEvents({ integrated_renxt_fundraising: { account_id: account.id } });
      toast(t('Enabled donation upsync.'), {
        type: 'success',
      });
      onSuccess();
    } catch (error) {
      toast(t("Couldn't update setting. {{error}}", { error }), {
        type: 'error',
      });
    }
  }

  const rightActions = [
    <Button key={'cancel'} onClick={onClose}>
      {t('Cancel')}
    </Button>,
    <Button disabled={!selectedFund} key={'confirm'} onClick={handleConfirm} type={'primary'}>
      {t('Confirm')}
    </Button>,
  ];

  return (
    <Modal
      allowOverflow
      description={t(
        'Funds represent how donors expect you to use their gifts. Donations earned through Feathr will be assigned to the selected fund.',
      )}
      onClose={onClose}
      opened={opened}
      rightActions={rightActions}
      title={t('Select a fund')}
    >
      <BBFundSelect
        integration={integration}
        label={t('Fund')}
        onChange={handleFundChange}
        value={selectedFund?.id}
      />
    </Modal>
  );
}

export default observer(EnableDonationSyncModal);
